import React, { useState, KeyboardEvent } from 'react';
import { Box, Card, CardContent, Typography, TextField, Chip, IconButton, Button, MenuItem, Stack, Divider, Tooltip, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Storage } from '../../Types/Storage';
import { StorageHealthSetupInput, StoragePurpose } from '../../Types/StorageHealth';

interface SetupModeProps {
    storage: Storage;
    initial?: Partial<StorageHealthSetupInput>;
    onSave: (data: StorageHealthSetupInput) => Promise<void> | void;
    onCancel?: () => void;
    saving?: boolean;
}

const purposeOptions: { value: StoragePurpose; label: string }[] = [
    { value: 'seed', label: 'Seed' },
    { value: 'freshpack', label: 'Fresh Pack' },
    { value: 'process', label: 'Process' },
];

const SetupMode: React.FC<SetupModeProps> = ({ storage, initial, onSave, onCancel, saving }) => {
    const [varietyInput, setVarietyInput] = useState('');
    const [varieties, setVarieties] = useState<string[]>(initial?.varieties || []);
    const [purpose, setPurpose] = useState<StoragePurpose>(initial?.purpose || 'process');
    const [targetCo2, setTargetCo2] = useState<number>(initial?.targetCo2 ?? 2500);
    const [targetCfm, setTargetCfm] = useState<number>(initial?.targetCfm ?? 18);
    const [error, setError] = useState<string | null>(null);

    const removeVariety = (v: string) => setVarieties(prev => prev.filter(p => p !== v));

    const handleSave = async () => {
        setError(null);
        if (varieties.length === 0) {
            setError('Please add at least one variety');
            return;
        }
        if (targetCo2 < 100 || targetCo2 > 20000) {
            setError('Max CO₂ should be between 100 and 20000 ppm');
            return;
        }
        await onSave({
            locationId: storage.id,
            varieties,
            purpose,
            targetCo2: Math.round(targetCo2),
            targetCfm: Number(targetCfm)
        });
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box mb={2}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Welcome to Storage Health Setup
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Let's capture a few seasonal details for <strong>{storage.name}</strong>. These guide health scoring and alerts. You'll only do this at the start of the season (or when editing later).
                    </Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    You can adjust these settings anytime from the gear icon in monitor mode.
                </Alert>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                    <TextField
                        select
                        label="Storage Purpose"
                        value={purpose}
                        onChange={e => setPurpose(e.target.value as StoragePurpose)}
                        size="small"
                        fullWidth
                        helperText="Select how this storage will primarily be used this season."
                    >
                        {purposeOptions.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Potato Varieties</Typography>
                        <Box display="flex" gap={1} mb={1}>
                            <TextField
                                label="Add Variety"
                                size="small"
                                value={varietyInput}
                                onChange={e => setVarietyInput(e.target.value)}
                                fullWidth
                            />
                        </Box>
                        <Box display="flex" gap={1} flexWrap="wrap" minHeight={40}>
                            {varieties.map(v => (
                                <Chip
                                    key={v}
                                    label={v}
                                    onDelete={() => removeVariety(v)}
                                    deleteIcon={<DeleteIcon />}
                                    sx={{ mb: 1 }}
                                />
                            ))}
                            {varieties.length === 0 && (
                                <Typography variant="caption" color="text.secondary">No varieties added yet</Typography>
                            )}
                        </Box>
                        {varieties.length === 0 && (
                            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                {['Russet Burbank', 'Yukon Gold', 'Ranger Russet', 'Umatilla'].map(s => (
                                    <Chip key={s} label={s} size="small" variant="outlined" onClick={() => { if (!varieties.includes(s)) setVarieties(p => [...p, s]); }} sx={{ cursor: 'pointer' }} />
                                ))}
                            </Box>
                        )}
                    </Box>

                    <TextField
                        label="Max CO₂ (ppm)"
                        type="number"
                        size="small"
                        value={targetCo2}
                        onChange={e => setTargetCo2(Number(e.target.value))}
                        inputProps={{ min: 100, max: 20000, step: 50 }}
                        fullWidth
                        helperText="Typical range 1500–5000 ppm depending on purpose."
                    />

                    <TextField
                        label="Target CFM"
                        type="number"
                        size="small"
                        value={targetCfm}
                        onChange={e => setTargetCfm(Number(e.target.value))}
                        inputProps={{ min: 1, max: 1000, step: 1 }}
                        fullWidth
                        helperText="Baseline airflow target. Adjust for pile depth & variety."
                    />

                    {error && <Typography color="error" variant="caption">{error}</Typography>}

                    <Divider sx={{ mt: 1 }} />
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                        {onCancel && (
                            <Button onClick={onCancel} disabled={!!saving}>Cancel</Button>
                        )}
                        <Button variant="contained" onClick={handleSave} disabled={!!saving}>
                            {saving ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default SetupMode;
